// TP-Friendlies mock — mirrors the M1 backend response shapes 1:1 so the browse +
// apply UI runs with REACT_APP_FR_MOCK=1 and no backend. Real screenshots/evidence
// are taken against the live stack; this exists for offline UI work + Jest.
//
// Card shape = FriendlyRequestFeedSerializer: club/country/team_category(+label)/
// team_label/date{…}/start_date/location{…}/tier{…}/context_line/status/expires_on/
// created_at + viewer-scoped my_application + saved. `start_date` is the coalesced
// sort date (camp→window start) the feed sorts + groups on.

const iso = (d) => d.toISOString().slice(0, 10);
const addDays = (base, n) => {
  const d = new Date(base);
  d.setDate(d.getDate() + n);
  return d;
};
const monthDay = (d) =>
  d.toLocaleDateString("en-US", { month: "long", day: "numeric" });

const TODAY = new Date();

const CH = { name: "Switzerland", code: "ch" };
const DE = { name: "Germany", code: "de" };
const TR = { name: "Türkiye", code: "tr" };

// Deterministic-ish spread across the next weeks so the week strip + date groups
// have staffed buckets: two this-week, two next-week, one pre-season, one camp.
const d1 = addDays(TODAY, 2); // this week, fixed
const d2 = addDays(TODAY, 4); // this week, fixed (applied)
const d3s = addDays(TODAY, 9); // next week, flexible range start
const d3e = addDays(TODAY, 16);
const d4 = addDays(TODAY, 12); // next week, fixed (saved)
const d5 = addDays(TODAY, 26); // pre-season, fixed
const dCampS = addDays(TODAY, 60); // camp window
const dCampE = addDays(TODAY, 70);

let REQUESTS = [
  {
    id: 101,
    club: { id: 1, name: "FC Wohlen", crest: "" },
    country: CH,
    team_category: "first_team",
    team_category_label: "First team",
    team_label: "",
    start_date: iso(d1),
    date: { is_flexible: false, date_start: iso(d1), date_end: null, kickoff_time: "17:00", phrase: monthDay(d1) },
    location: { mode: "host", country: CH, region: "", is_destination: false },
    max_distance_km: null,
    tier: { mode: "range", min: 1, max: 2, display: "Tier 1–2" },
    context_line: "Full 90 minutes · natural grass · cost sharing possible",
    status: "open",
    expires_on: iso(d1),
    created_at: addDays(TODAY, -1).toISOString(),
    my_application: null,
    saved: false,
  },
  {
    id: 102,
    club: { id: 2, name: "FC Aarau", crest: "" },
    country: CH,
    team_category: "youth_2nd",
    team_category_label: "Youth · 2nd team",
    team_label: "U19",
    start_date: iso(d2),
    date: { is_flexible: false, date_start: iso(d2), date_end: null, kickoff_time: null, phrase: monthDay(d2) },
    location: { mode: "travel", country: CH, region: "", is_destination: false },
    max_distance_km: 80,
    tier: { mode: "three_or_lower", min: null, max: null, display: "Tier 3 or lower" },
    context_line: "",
    status: "open",
    expires_on: iso(d2),
    created_at: addDays(TODAY, -3).toISOString(),
    my_application: { state: "applied" }, // quiet Applied state
    saved: false,
  },
  {
    id: 103,
    club: { id: 3, name: "SV Sandhausen", crest: "" },
    country: DE,
    team_category: "first_team_youth",
    team_category_label: "First team + Youth",
    team_label: "",
    start_date: iso(d3s),
    date: { is_flexible: true, date_start: iso(d3s), date_end: iso(d3e), kickoff_time: null, phrase: `${monthDay(d3s)}–${d3e.getDate()}` },
    location: { mode: "neutral", country: DE, region: "", is_destination: false },
    max_distance_km: null,
    tier: { mode: "any", min: null, max: null, display: "Any level" },
    context_line: "Flexible on the exact day within the window.",
    status: "open",
    expires_on: iso(d3e),
    created_at: addDays(TODAY, -2).toISOString(),
    my_application: null,
    saved: false,
  },
  {
    id: 104,
    club: { id: 4, name: "FC Thun", crest: "" },
    country: CH,
    team_category: "first_team",
    team_category_label: "First team",
    team_label: "",
    start_date: iso(d4),
    date: { is_flexible: false, date_start: iso(d4), date_end: null, kickoff_time: "15:00", phrase: monthDay(d4) },
    location: { mode: "host", country: CH, region: "", is_destination: false },
    max_distance_km: null,
    tier: { mode: "range", min: 2, max: 3, display: "Tier 2–3" },
    context_line: "",
    status: "open",
    expires_on: iso(d4),
    created_at: addDays(TODAY, -1).toISOString(),
    my_application: null,
    saved: true, // pre-saved bookmark
  },
  {
    id: 105,
    club: { id: 5, name: "SC Freiburg II", crest: "" },
    country: DE,
    team_category: "first_team",
    team_category_label: "First team",
    team_label: "",
    start_date: iso(d5),
    date: { is_flexible: false, date_start: iso(d5), date_end: null, kickoff_time: null, phrase: monthDay(d5) },
    location: { mode: "travel", country: DE, region: "", is_destination: false },
    max_distance_km: 150,
    tier: { mode: "range", min: 1, max: 3, display: "Tier 1–3" },
    context_line: "Pre-season shape-up · behind closed doors.",
    status: "open",
    expires_on: iso(d5),
    created_at: addDays(TODAY, -5).toISOString(),
    my_application: null,
    saved: false,
  },
  {
    id: 106,
    club: { id: 6, name: "FC Schaffhausen", crest: "" },
    country: CH, // HOME is Switzerland …
    team_category: "youth_2nd",
    team_category_label: "Youth · 2nd team",
    team_label: "U23",
    start_date: iso(dCampS),
    date: { is_flexible: false, date_start: null, date_end: null, kickoff_time: null, phrase: `${monthDay(dCampS)}–${dCampE.getDate()}` },
    // … but the camp surfaces under its DESTINATION (Türkiye / Belek), binding rule.
    location: { mode: "camp", country: TR, region: "Belek", is_destination: true },
    max_distance_km: null,
    tier: { mode: "range", min: 1, max: 2, display: "Tier 1–2" },
    context_line: "3 matches wanted across the window · grass pitches at the resort.",
    status: "open",
    expires_on: iso(dCampE),
    created_at: addDays(TODAY, -4).toISOString(),
    camp_multiple_matches: true,
    my_application: null,
    saved: false,
  },
];

const withinRange = (dateStr, from, to) => {
  if (!dateStr) return true;
  if (from && dateStr < from) return false;
  if (to && dateStr > to) return false;
  return true;
};

// Week-strip aggregate = the ISO-week(6)+month bucketing the backend emits
// (fr-be-builder 8426a23): Monday-anchored weeks, first = "This week", then months.
const mondayOf = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  const dow = (x.getDay() + 6) % 7; // Mon=0
  x.setDate(x.getDate() - dow);
  return x;
};
const shortMD = (d) => d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
const buildBuckets = (rows, weeks = 6) => {
  const wk0 = mondayOf(TODAY);
  const horizonEnd = addDays(wk0, weeks * 7);
  const weekCounts = {};
  const months = {};
  rows.forEach((r) => {
    if (!r.start_date) return;
    const d = new Date(r.start_date + "T00:00:00");
    if (d >= wk0 && d < horizonEnd) {
      const wm = mondayOf(d);
      const key = iso(wm);
      weekCounts[key] = (weekCounts[key] || 0) + 1;
    } else if (d >= horizonEnd) {
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      months[key] = (months[key] || 0) + 1;
    }
  });
  const out = [];
  for (let i = 0; i < weeks; i += 1) {
    const from = addDays(wk0, i * 7);
    const to = addDays(from, 6);
    const key = iso(from);
    out.push({
      kind: "week", key, date_from: key, date_to: iso(to),
      label: i === 0 ? "This week" : `${shortMD(from)}–${to.getDate()}`,
      count: weekCounts[key] || 0,
    });
  }
  Object.keys(months).sort().forEach((key) => {
    const [y, m] = key.split("-").map(Number);
    out.push({ kind: "month", key, label: new Date(y, m - 1, 1).toLocaleDateString("en-US", { month: "long", year: "numeric" }), count: months[key] });
  });
  return out;
};

const applyFilters = (rows, params = {}) => {
  const p = params instanceof URLSearchParams ? Object.fromEntries(params.entries()) : params || {};
  let out = rows.slice();
  if (p.team_category) out = out.filter((r) => r.team_category === p.team_category);
  if (p.country) {
    const c = String(p.country).toLowerCase();
    out = out.filter((r) => (r.location?.country?.name || "").toLowerCase() === c);
  }
  if (p.tier) {
    const t = Number(p.tier);
    out = out.filter((r) => {
      const { mode, min, max } = r.tier;
      if (mode === "any") return true;
      if (mode === "range") return min <= t && max >= t;
      if (mode === "three_or_lower") return t >= 3;
      return false;
    });
  }
  if (p.date_from || p.date_to) out = out.filter((r) => withinRange(r.start_date, p.date_from, p.date_to));
  if (p.saved_only === "1" || p.saved_only === "true") out = out.filter((r) => r.saved);
  out.sort((a, b) => (a.start_date || "9999").localeCompare(b.start_date || "9999"));
  return out;
};

const friendliesMock = {
  getFeed: async (params) => {
    const rows = applyFilters(REQUESTS, params);
    return { success: true, data: rows, count: rows.length, buckets: buildBuckets(REQUESTS) };
  },
  getRequest: async (id) => {
    const r = REQUESTS.find((x) => String(x.id) === String(id));
    return r ? { success: true, data: r } : { success: false, error: "Not found" };
  },
  apply: async (id, { note } = {}) => {
    const r = REQUESTS.find((x) => String(x.id) === String(id));
    if (!r) return { success: false, error: "Not found" };
    if (r.my_application) return { success: true, data: { id, state: r.my_application.state, already_applied: true } };
    r.my_application = { state: "applied" };
    return {
      success: true,
      data: { id, state: "applied", auto_message: `SC Kriens · First team: interested in your friendly on ${r.date.phrase}`, note },
    };
  },
  toggleSave: async (id) => {
    const r = REQUESTS.find((x) => String(x.id) === String(id));
    if (!r) return { success: false, error: "Not found" };
    r.saved = !r.saved;
    return { success: true, data: { saved: r.saved } };
  },
  getSaved: async () => {
    const rows = REQUESTS.filter((r) => r.saved);
    return { success: true, data: rows, count: rows.length };
  },
  // ── M3 owner/applicant stubs (mock-mode only; real stack used for M3 evidence) ──
  createRequest: async (body) => ({ success: true, data: { id: 999, ...body, status: "open" } }),
  updateRequest: async (id, body) => ({ success: true, data: { id, ...body } }),
  getMine: async (state) => {
    const rows = REQUESTS.filter((r) => (state ? r.status === state : true)).map((r) => ({
      ...r, application_count: 0, matches_agreed: 0,
    }));
    return { success: true, data: rows, count: rows.length };
  },
  getApplications: async () => ({ success: true, data: [], count: 0 }),
  closeRequest: async (id, outcome) => ({ success: true, data: { id, status: outcome === "matched" ? "matched" : "closed" } }),
  getMyApplications: async () => ({ success: true, data: [], count: 0 }),
  startChat: async () => ({ success: true, data: { channel_id: "messaging:mock" } }),
  dismissApplication: async (id) => ({ success: true, data: { id, ok: true } }),
  markAgreed: async (id) => ({ success: true, data: { id, matches_agreed: 1, request_status: "open" } }),

  // Test seam — reset mutable state between Jest cases.
  __reset: () => {
    REQUESTS = REQUESTS.map((r) => ({ ...r }));
  },
};

export default friendliesMock;
